import URI from "urijs";
import { parseSearchParams } from ".";
import {
  PDE,
  PrescriptionDrug,
  CoverageInfo,
  BeneficiaryAndApiHeaders,
  Beneficiary,
  IdParts,
  Pharmacy,
  PharmacyType,
  OecStatus,
} from "../@types";
import { loginBene, getCSRBeneInfo, getBeneOecStatus } from "../api";
import { ApiResponseError, logError } from "./errors";
import { drugsAreDuplicates, uppercaseFirst } from "./prescriptionHelpers";

// Intended to be used like so: call iniitateLogin() -> redirect to MBP & SLS for login ->
export async function getBeneficiaryInfo(
  code: string
): Promise<BeneficiaryAndApiHeaders> {
  // Ask the API to kindly fetch the details of our beneficiary
  const beneInfo = await loginBene(code);

  return beneInfo;
}

export async function getBeneInfoForCSR(
  code: string
): Promise<BeneficiaryAndApiHeaders> {
  return await getCSRBeneInfo(code);
}

export function getSLSCode(search: string): string {
  const { code } = parseSearchParams(URI.parseQuery(search));
  return code;
}

export const doesNotInclude = (
  acc: PrescriptionDrug[],
  { ndc, name }: { ndc: string; name: string }
): boolean => {
  return acc.every(
    ({ ndc: currentNDC, name: currentName }) =>
      currentNDC !== ndc && currentName !== name
  );
};

export const extractPDEsFromBene = ({
  pdes,
}: {
  pdes: PDE[];
}): PrescriptionDrug[] => {
  const isWithinLastYear = (service_date: string): boolean => {
    const pdeDate = new Date(service_date).getTime();
    const thisYear = new Date().getFullYear();
    const aYearAgo = new Date().setFullYear(thisYear - 1);

    if (pdeDate > aYearAgo) {
      return true;
    }

    return false;
  };

  return pdes.reduce((accumulator, currentPDE) => {
    if (
      /* istanbul ignore next */
      currentPDE.service_date // FIXME: Lovely little hack because the fake data currently doesn't include a service date.
        ? isWithinLastYear(currentPDE.service_date)
        : true
    ) {
      const theDrug = currentPDE.drug;
      if (theDrug.name !== "" && doesNotInclude(accumulator, theDrug)) {
        accumulator.push(theDrug);
      }
    }
    return accumulator;
  }, [] as PrescriptionDrug[]);
};

interface PlanIdParts extends IdParts {
  contract_year: string;
}

// todo: rename this to arePlansTheSame
export const isCurrentPlan = (
  coverageInfo: CoverageInfo | undefined,
  plan: PlanIdParts
): boolean => {
  if (!coverageInfo) return false;

  return (
    coverageInfo.plan_id === plan.plan_id &&
    coverageInfo.segment_id === plan.segment_id &&
    coverageInfo.contract_id === plan.contract_id &&
    (coverageInfo.contract_year === plan.contract_year ||
      coverageInfo.coverage_year === plan.contract_year)
  );
};

export const deduplicatePrescriptions = (
  prescriptions: PrescriptionDrug[]
): PrescriptionDrug[] => {
  return prescriptions.filter(
    (a, index) =>
      prescriptions.findIndex(b => drugsAreDuplicates(a, b)) === index
  );
};

export const getBeneFirstAndLastName = (
  beneficiary: Beneficiary,
  t: (key: string) => string
): string => {
  const firstName =
    beneficiary.firstname && beneficiary.firstname.length
      ? uppercaseFirst(beneficiary.firstname[0])
      : "";
  return firstName || beneficiary.lastname
    ? `${firstName} ${uppercaseFirst(beneficiary.lastname)}`.trim()
    : t("bene_landing.name_not_available");
};

export const getBeneFirstNameAndLastInitial = (
  beneficiary: Beneficiary,
  t: (key: string) => string
): string => {
  const firstName =
    beneficiary.firstname && beneficiary.firstname.length
      ? uppercaseFirst(beneficiary.firstname[0])
      : "";
  if (firstName && beneficiary.lastname) {
    return `${firstName} ${beneficiary.lastname[0].toUpperCase()}.`.trim();
  } else if (beneficiary.lastname) {
    return uppercaseFirst(beneficiary.lastname).trim();
  } else if (firstName) {
    return firstName;
  } else {
    return t("bene_landing.name_not_available");
  }
};

export const getPharmacyTypeFromSavedPharmacies = (
  pharmacies: Pharmacy[],
  mailOrder: boolean
): PharmacyType => {
  let pharmacyType = PharmacyType.NONE;
  const isRetail = pharmacies.length > 0;
  const isMail = mailOrder;

  if (isRetail && isMail) {
    pharmacyType = PharmacyType.MAIL_AND_RETAIL;
  } else if (isRetail) {
    pharmacyType = PharmacyType.RETAIL;
  } else if (isMail) {
    pharmacyType = PharmacyType.MAIL;
  }

  return pharmacyType;
};

export const fetchOECStatus = async (
  year: string
): Promise<OecStatus | undefined> => {
  try {
    const status = await getBeneOecStatus(year);
    return status;
  } catch (_e) {
    const e = _e as Error | ApiResponseError;
    logError("Failed to get beneficiary OEC status (LoginCallbackHandler)", e);
    return undefined;
  }
};
